import {
  StyledNotificationSheet,
  StyledNotificationTitleContainer,
  StyledCustomScrollbarWrapper,
  CloseAllButton,
  DownloadButton,
  MarkAllReadButton,
  SummaryButton,
  NotificationActionBar,
  ActionBarRight,
  GroupByChipGroup,
  NDTitle
} from './styles';
import { Chip, ChoiceChips, SearchInput, TextButton, IconFilterList } from 'cdk-radial';
import { useApplicationStore } from '../../Store/index';
import { groupByDate, groupByApplication } from './notificationGrouping';
import { useSideSheet } from '../../contexts/SideSheet';
import { SideSheetHeader } from '../SideSheet/SideSheet';
import { useTranslation } from 'react-i18next';
import NotificationList from './NotificationList';
import { NotificationDetail } from './NotificationDetail';
import { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { INotification } from './Notification';
import { getNotificationInsights } from '../../util/api';
import useAuthToken from '../../hooks/useAuthToken';
import { NotificationSummaryPopup } from './NotificationSummaryPopup';
import {
  toModelInput,
  parseInsightsResponse,
  notificationsSignature,
  linkifySummary
} from './notificationInsights';
import {
  useUpdateNotification,
  useDeleteNotification,
  useDeleteAllNotification,
  useMarkAllNotificationsRead,
} from '../../hooks/queries';
import { useNotificationStore } from './NotificationStore';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { useTheme } from '../../contexts/Theme';
import { useUserProfile } from '../../hooks/queries';
import FocusTrap from 'focus-trap-react';
import { getLocalizationValues } from '../UnifyAdministration/utils';

type NotificationSort =
  | 'newest-first'
  | 'oldest-first'
  | 'unread-first';

const NOTIFICATION_SORT_OPTIONS: Array<{
  labelKey: string;
  defaultLabel: string;
  value: NotificationSort;
}> = [
  {
    labelKey: 'notification.sheet.sortNewestFirst',
    defaultLabel: 'Newest First',
    value: 'newest-first',
  },
  {
    labelKey: 'notification.sheet.sortOldestFirst',
    defaultLabel: 'Oldest First',
    value: 'oldest-first',
  },
  {
    labelKey: 'notification.sheet.sortUnreadFirst',
    defaultLabel: 'Unread First',
    value: 'unread-first',
  },
];

type NotificationFilter =
  | 'all'
  | 'unread'
  | 'alerts'
  | 'feature'
  | 'activities';

const NOTIFICATION_FILTERS: Array<{
  labelKey: string;
  value: NotificationFilter;
}> = [
    { labelKey: 'notification.sheet.filterAll', value: 'all' },
    { labelKey: 'notification.sheet.filterUnread', value: 'unread' },
    { labelKey: 'notification.sheet.filterAlerts', value: 'alerts' },
    { labelKey: 'notification.sheet.filterFeature', value: 'feature' },
    { labelKey: 'notification.sheet.filterActivities', value: 'activities' }
  ];

const matchesNotificationFilter = (
  notification: INotification,
  filter: NotificationFilter
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'unread') return !notification.read;
  if (filter === 'alerts') {
    return notification.type === 'outage' || notification.type === 'alert';
  }
  if (filter === 'feature') return notification.type === 'release';
  if (filter === 'activities') return notification.type === 'message';

  return false;
};

const NOTIFICATION_LOCALE_MAP_KEYS = new Set([
  'titleLocale',
  'subjectLocale',
  'bodyLocale',
  'externalUrlLocale',
]);

const flattenNotificationValues = (value: unknown, seen: WeakSet<object> = new WeakSet()): string[] => {
  if (value === null || value === undefined) return [];
  if (typeof value !== 'object') return [String(value)];
  if (seen.has(value)) return [];
  seen.add(value);
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenNotificationValues(item, seen));
  }
  return Object.entries(value)
    .filter(([key]) => !NOTIFICATION_LOCALE_MAP_KEYS.has(key))
    .flatMap(([, item]) => flattenNotificationValues(item, seen));
};

const getNotificationTimestamp = (notification: INotification): number => {
  const value = (notification as any).receivedDate ??
    (notification as any).createdDate ??
    (notification as any).timestamp;
  const timestamp = typeof value === 'number' ? value : new Date(value ?? 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

function SummaryStarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ marginLeft: '4px' }}>
      <path
        d="M12 2L14.2 9.8L22 12L14.2 14.2L12 22L9.8 14.2L2 12L9.8 9.8L12 2Z"
        fill="#196FE1"
      />
      <path
        d="M18 4L18.8 6.8L21.5 7.5L18.8 8.2L18 11L17.2 8.2L14.5 7.5L17.2 6.8L18 4Z"
        fill="#4DA3FF"
      />
    </svg>
  );
}

function SortChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      aria-hidden="true"
      height="16"
      style={{
        display: 'block',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 120ms ease',
      }}
      viewBox="0 0 24 24"
      width="16"
    >
      <path
        d="M7 9.5L12 14.5L17 9.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function NotificationSheet() {
  const { data: userProfile } = useUserProfile();
  const { darkMode } = useTheme();
  const { setSideSheetContent } = useSideSheet();
  const { t } = useTranslation();
  const NOTIFICATION_INSIGHTS = useFeatureFlag(
    'UNIFY_UI_NOTIFICATION_INSIGHTS'
  );
  const toggle = () => setSideSheetContent(null);
  const [selectedNotification, setSelectedNotification] =
    useState<INotification>();
  const [showDetail, setShowDetail] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryText, setSummaryText] = useState('Loading');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchFilters, setShowSearchFilters] = useState(false);
  const [activeFilter, setActiveFilter] =
    useState<NotificationFilter>('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSortMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortMenu]);


  const groupBy = useNotificationStore((state: any) => state.groupBy);
  const setGroupBy = useNotificationStore((state: any) => state.setGroupBy);
  const sortOrder = useNotificationStore((state: any) => state.sortOrder);
  const setSortOrder = useNotificationStore((state: any) => state.setSortOrder);
  const applications = useApplicationStore((state: any) => state.applications);

  const handleGroupByChange = (value: string) => {
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, { Action: `Sort By ${value === 'application' ? 'Application' : 'Date'}` });
    setGroupBy(value as 'date' | 'application');
  };

  const handleSearchChange = (value: string) => {
    if (!searchTerm && value) {
      trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, { Action: 'Search' });
    }
    setSearchTerm(value);
  };

  const { mutate: updateNotification } = useUpdateNotification();
  const { mutate: deleteNotification } = useDeleteNotification();
  const { mutate: deleteAllNotification } = useDeleteAllNotification();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();
  const notifications = useNotificationStore(
    (state: any) => state.notifications
  );
  const read = useNotificationStore((state: any) => state.read);
  const count = useNotificationStore((state: any) => state.count);
  const unreadCount = useNotificationStore((state: any) => state.unreadCount);
  const remove = useNotificationStore((state: any) => state.remove);
  const insightsSummary = useNotificationStore((state: any) => state.insightsSummary);
  const insightsSignature = useNotificationStore((state: any) => state.insightsSignature);
  const setInsights = useNotificationStore((state: any) => state.setInsights);
  const selectedNotificationToOpen = useNotificationStore((state: any) => state.selectedNotificationToOpen);
  const setSelectedNotificationToOpen = useNotificationStore((state: any) => state.setSelectedNotificationToOpen);
  const { trackEvent } = useAnalytics();
  const { openCtaPane } = usePaneManagement();
  const token = useAuthToken();

  useEffect(() => {
    if (selectedNotificationToOpen) {
      setSelectedNotification(selectedNotificationToOpen);
      setShowDetail(true);
      setSelectedNotificationToOpen(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNotificationToOpen]);

  const visibleNotifications = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
    const list = Array.isArray(notifications) ? notifications : [];

    return list.filter((notification: INotification) => {
      if (
        notification.deleted ||
        !matchesNotificationFilter(notification, activeFilter)
      ) {
        return false;
      }

      if (!normalizedSearch) return true;

      const localizedNotification = getLocalizationValues(
        userProfile,
        notification
      );
      const searchableText = [
        ...flattenNotificationValues(notification),
        ...flattenNotificationValues(localizedNotification),
      ]
        .join(' ')
        .toLocaleLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [activeFilter, notifications, searchTerm, userProfile]);

  const sortedNotifications = useMemo(() => {
    const indexedNotifications = visibleNotifications.map(
      (notification, originalIndex) => ({ notification, originalIndex })
    );
    const newestFirst = (
      left: (typeof indexedNotifications)[number],
      right: (typeof indexedNotifications)[number]
    ) =>
      getNotificationTimestamp(right.notification) -
        getNotificationTimestamp(left.notification) ||
      left.originalIndex - right.originalIndex;

    if (sortOrder === 'newest-first') {
      return indexedNotifications.sort(newestFirst).map(({ notification }) => notification);
    }

    if (sortOrder === 'oldest-first') {
      return indexedNotifications
        .sort(
          (left, right) =>
            getNotificationTimestamp(left.notification) -
              getNotificationTimestamp(right.notification) ||
            left.originalIndex - right.originalIndex
        )
        .map(({ notification }) => notification);
    }

    if (sortOrder === 'unread-first') {
      return indexedNotifications
        .sort((left, right) => {
          const readDifference = Number(left.notification.read) - Number(right.notification.read);
          return readDifference || newestFirst(left, right);
        })
        .map(({ notification }) => notification);
    }

    return indexedNotifications.sort(newestFirst).map(({ notification }) => notification);
  }, [sortOrder, visibleNotifications]);

  const handleSearchFiltersToggle = () => {
    setShowSearchFilters((isOpen) => {
      if (isOpen) {
        setSearchTerm('');
        setActiveFilter('all');
      }
      return !isOpen;
    });
  };

  const title =
    count > 0
      ? `${t('notification.sheet.title')} (${count})`
      : t('notification.sheet.title');

  const detailBackHandler = () => {
    setShowDetail(false);
  };

  const onDeleteHandler = (notification: INotification) => {
    if (!notification.deleted) {
      remove(notification);
      trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
        'Action': 'Delete',
        Title: notification?.title
      });
    }
    !notification.deleted && deleteNotification(notification);
  };

  const handleDeleteAllClick = () => {
    const messageIds = notifications.filter((notification: any) => !notification.deleted && notification.type !== 'outage')
    .map((notification: any) => notification.messageId);
    deleteAllNotification(messageIds);
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      'Action': 'Delete All',
    });
  };

  const onToggleReadHandler = (notification: INotification) => {
    const nextRead = !notification.read;
    updateNotification({ notification, read: nextRead });
    read(notification, nextRead);
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: notification?.title,
      Type: notification?.type,
      Action: nextRead ? 'Mark Read' : 'Mark Unread',
    });
  };

  const handleMarkAllReadClick = () => {
    markAllRead();
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Action: 'Mark All Read',
    });
  };

  const onSelectHandler = (notification: INotification) => {
    if (notification.ctaUrl) {
      setSideSheetContent(null);
      openCtaPane(notification.ctaUrl);
    } else {
      setShowDetail(true);
      setSelectedNotification(notification);
    }

    !notification.read && updateNotification({ notification, read: true });
    if (!notification.read) {
      read(notification, true);
    }
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: notification?.title,
      Type: notification?.type,
      'Action': 'Open'
    });
  };

  const onDetailClose = () => {
    setShowDetail(false);
  };

  const sheetRef = useRef<HTMLDivElement>(null);

  const stripHtml = (html: string = ""): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

  const getFilteredNotifications = () => {
    return notifications.filter((notification: any) =>
      !notification.deleted &&
      notification.type !== "outage"
    );
  };

  const getInsightsNotifications = () => {
    return notifications.filter((notification: any) => !notification.deleted);
  };

const getLocalTime = (ms: number) =>
  new Date(ms).toLocaleString();

 const exportNotificationsToCSV = () => {
  console.log("Exporting notifications to CSV" + JSON.stringify(notifications));
  if (!notifications || notifications.length === 0) return;

  const filteredNotifications = getFilteredNotifications();

  if (filteredNotifications.length === 0) return;

  const headers = [
    "Title",
    "Subject",
    "Body",
    "Products Affected",
    "Date",
    "Links",
  ];
  const rows = filteredNotifications.map((notification: any) => [
    getLocalizationValues(userProfile, notification).title ?? notification.title,
    getLocalizationValues(userProfile, notification).subject ?? notification.subject,
    stripHtml(
    getLocalizationValues(userProfile, notification).body ??
    notification.body ??
    ""
  ),
    notification.application ?? "",
    notification.receivedDate ? getLocalTime(notification.receivedDate): "",
    notification.externalUrl ?? "",
  ]);

   const csvBody =
    [headers, ...rows]
      .map((row) =>
        row
          .map((cell: any) =>
            `"${String(cell).replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\n");

  const csvContent = "\uFEFF" + csvBody;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "Notifications.csv";
  link.click();

  URL.revokeObjectURL(url);
};


  // Signature of the current (non-deleted, non-outage) notification set. When
  // this differs from what the cached insights were built for, a notification
  // has been added/removed and we need to call the API again.
  const currentSignature = useMemo(
    () => notificationsSignature(getInsightsNotifications()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notifications]
  );
  const hasFreshInsights =
    !!currentSignature && insightsSignature === currentSignature;

  
  const refreshInsights = async (): Promise<{ summary: string } | null> => {
    const list = getInsightsNotifications();
    if (list.length === 0) return null;
    try {
      const raw = await getNotificationInsights(
        { notifications: toModelInput(list, userProfile?.locale || 'en_US'), locale: userProfile?.locale || 'en_US' },
        token
      );
      const response = parseInsightsResponse(raw);
      const summary = response?.summary || '';
      if (summary) {
        setInsights(summary, notificationsSignature(list));
      }
      return { summary };
    } catch (error) {
      console.error('Error fetching notification insights:', error);
      return null;
    }
  };

  const handleSummaryClick = async () => {
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, { Action: 'AI Summary' });
    setShowSummary(true);

    const hasNotifications = getInsightsNotifications().length > 0;
    const emptyMessage = hasNotifications
      ? t('notification.sheet.summaryUnavailable')
      : t('notification.sheet.nomessage');
    if (hasFreshInsights) {
      setSummaryText(insightsSummary || emptyMessage || '');
      return;
    }
    setSummaryText('Loading');
    setIsSummaryLoading(true);
    try {
      const result = await refreshInsights();
      setSummaryText(result?.summary || emptyMessage || '');
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleSummaryNotificationClick = (id: string) => {
    const target = notifications.find(
      (n: any) => n.messageId === id && !n.deleted
    );
    if (!target) return;

    setShowSummary(false);
    setSelectedNotification(target);
    setShowDetail(true);
    if (!target.read) {
      updateNotification({ notification: target, read: true });
      read(target, true);
    }
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: target?.title,
      Type: target?.type,
      Action: 'Open'
    });
  };

  const notificationGroups = useMemo(() => {
    const groups = groupBy === 'application'
      ? groupByApplication(
        sortedNotifications,
        applications,
        t('notification.sheet.groupOther')
      )
      : groupByDate(sortedNotifications, {
          today: t('notification.sheet.groupToday'),
          yesterday: t('notification.sheet.groupYesterday'),
          last7Days: t('notification.sheet.groupLast7Days'),
          older: t('notification.sheet.groupOlder'),
        });

    const notificationOrder = new Map(
      sortedNotifications.map((notification, index) => [notification, index])
    );
    const getOrder = (notification: INotification) =>
      notificationOrder.get(notification) ?? Number.MAX_SAFE_INTEGER;

    return groups.map((group) => ({
      ...group,
      items: [...group.items].sort(
        (left, right) => getOrder(left) - getOrder(right)
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedNotifications, groupBy, applications, t]);

  const selectedSortOption =
    NOTIFICATION_SORT_OPTIONS.find(({ value }) => value === sortOrder) ??
    NOTIFICATION_SORT_OPTIONS[0];

  return (
    <FocusTrap active={true}
    focusTrapOptions={{
      clickOutsideDeactivates: true,
                    tabbableOptions: {
                        displayCheck: 'none'
                    }
                }}>
    <StyledNotificationSheet data-testid="notification-sheet" ref={sheetRef}>
      <StyledNotificationTitleContainer data-testid="notification-title-container">
        <SideSheetHeader
          name={title}
          toggle={toggle}
          dataTestId="notification-tab-header"
        />
      </StyledNotificationTitleContainer>

      <StyledCustomScrollbarWrapper>
          <NotificationActionBar>
            {NOTIFICATION_INSIGHTS && getInsightsNotifications()?.length > 0 && (
              <SummaryButton
                data-testid="notification-ai-summary"
                onClick={handleSummaryClick}
                text={t('notification.sheet.summary')}
                icon={<SummaryStarIcon />}
                isIconTrailing
                darkMode={darkMode}
                tabIndex={0}
                isDisabled={isSummaryLoading}
              />
            )}
            <ActionBarRight>
              {unreadCount > 0 &&
                <MarkAllReadButton data-testid="notification-mark-all-read"
                  onClick={handleMarkAllReadClick}
                  text={t('notification.sheet.markAllRead')}
                  darkMode={darkMode}
                  tabIndex={0} />
              }
              {notifications && getFilteredNotifications()?.length > 0 &&
                <DownloadButton data-testid="notification-download-all"
                  onClick={exportNotificationsToCSV}
                  text={t('notification.sheet.download')}
                  darkMode={darkMode}
                  tabIndex={0} />
              }
              {notifications
                && notifications.filter((notification: any) => notification.type !== 'outage' && notification.deleted === false).length > 1
                &&
                <CloseAllButton data-testid="notification-close-all"
                  onClick={handleDeleteAllClick}
                  text={t('notification.sheet.clearall')}
                  darkMode={darkMode}
                  tabIndex={0} />
              }
            </ActionBarRight>
          </NotificationActionBar>
          {Array.isArray(notifications) &&
            notifications.filter((n: any) => !n.deleted).length > 0 && (
            <GroupByChipGroup style={{ boxSizing: 'border-box', minWidth: '0', width: 'auto' }}>
                <div
                  style={{
                    alignItems: 'start',
                    boxSizing: 'border-box',
                    display: 'grid',
                    gap: '8px',
                    gridTemplateColumns: 'minmax(max-content, 1fr) 165px',
                    // paddingRight: '5px',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: '0'
                  }}
                >
                  <ChoiceChips
                    dataTestId="notification-group-by"
                    id="notification-group-by"
                    name="notificationGroupBy"
                    onClick={(value: string) =>
                      value && handleGroupByChange(value)
                    }
                    options={[
                      { label: t('notification.sheet.groupByDate') as string, value: 'date' },
                      {
                        label: t('notification.sheet.groupByApplication') as string,
                        value: 'application',
                      },
                    ]}
                    value={groupBy}
                  />
                  <div
                    ref={sortMenuRef}
                    style={{
                      minWidth: 0,
                      position: 'relative',
                      width: '176px',
                    }}
                  >
                    <button
                      aria-expanded={showSortMenu}
                      aria-haspopup="menu"
                      data-testid="notification-sort"
                      onClick={() => setShowSortMenu((isOpen) => !isOpen)}
                      style={{
                        alignItems: 'center',
                        background: 'transparent',
                        border: '1px solid #b4b4b4',
                        borderRadius: '18px',
                        boxSizing: 'border-box',
                        color: darkMode ? '#ffffff' : '#196fe1',
                        cursor: 'pointer',
                        display: 'flex',
                        font: 'inherit',
                        // fontSize: '14px',
                        height: '36px',
                        justifyContent: 'space-between',
                        padding: '0 10px 0 12px',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                      type="button"
                    >
                      <span
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t(selectedSortOption.labelKey, {
                          defaultValue: selectedSortOption.defaultLabel,
                        })}
                      </span>
                      <SortChevronIcon isOpen={showSortMenu} />
                    </button>
                    {showSortMenu && (
                      <div
                        aria-label="Notification sorting"
                        role="menu"
                        style={{
                          background: darkMode ? '#2b2b2b' : '#ffffff',
                          border: '1px solid #b4b4b4',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                          boxSizing: 'border-box',
                          minWidth: '156px',
                          maxWidth: '220px',
                          padding: '4px 0',
                          position: 'absolute',
                          // right: 0,
                          top: 'calc(100% + 4px)',
                          zIndex: 20,
                        }}
                      >
                        {NOTIFICATION_SORT_OPTIONS.map(
                          ({ labelKey, defaultLabel, value }) => (
                            <button
                              aria-checked={sortOrder === value}
                              data-testid={`notification-sort-${value}`}
                              key={value}
                              onClick={() => {
                                setSortOrder(value);
                                setShowSortMenu(false);
                              }}
                              role="menuitemradio"
                              style={{
                                background:
                                  sortOrder === value
                                    ? darkMode
                                      ? '#454545'
                                      : '#eef4fd'
                                    : 'transparent',
                                border: 0,
                                boxSizing: 'border-box',
                                color: darkMode ? '#ffffff' : '#1f1f1f',
                                cursor: 'pointer',
                                display: 'block',
                                overflow: 'hidden',
                                padding: '8px 12px',
                                textAlign: 'left',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%',
                              }}
                              title={t(labelKey, { defaultValue: defaultLabel }) as string}
                              type="button"
                            >
                              {t(labelKey, { defaultValue: defaultLabel })}
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </GroupByChipGroup>
            )}
          {!showDetail &&
            Array.isArray(notifications) &&
            notifications.some((notification: INotification) => !notification.deleted) && (
              <div style={{ padding: '8px 16px' }}>
                <TextButton
                  data-testid="notification-search-filters-toggle"
                  onClick={handleSearchFiltersToggle}
                  text={t('notification.sheet.searchFilters')}
                  icon={<IconFilterList />}
                  isIconTrailing
                />
                {showSearchFilters && (
                  <div style={{ paddingTop: '8px' }}>
                    <SearchInput
                      clearButtonLabel="Clear search"
                      data-testid="notification-search"
                      id="notification-search"
                      name="notification-search"
                      onChange={handleSearchChange}
                      placeholder={t('notification.sheet.searchPlaceholder')}
                      value={searchTerm}
                      isAlwaysExpanded
                    />
                    <div
                      aria-label="Notification filters"
                      role="group"
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '2px',
                        overflowX: 'hidden',
                        margin: '0 -8px',
                        padding: '12px 0 4px'
                      }}
                    >
                      {NOTIFICATION_FILTERS.map(({ labelKey, value }) => (
                        <div key={value} style={{ flexShrink: 0 }}>
                          <Chip
                            label={t(labelKey)}
                            onClick={() => setActiveFilter(value)}
                            isSelected={activeFilter === value}
                            aria-pressed={activeFilter === value}
                          />
                        </div>
                      ))}

                    </div>
                  </div>
                )}
              </div>
            )}
          {notifications && visibleNotifications.length === 0 &&
            (searchTerm.trim() || activeFilter !== 'all') ?
            (<NDTitle style={{ padding: '24px 16px', textAlign: 'center' }}>
              {t('notification.sheet.noSearchResults')}
            </NDTitle>) : notifications ? (
              <NotificationList
                groups={notificationGroups}
                onSelect={onSelectHandler}
                onDelete={onDeleteHandler}
                onToggleRead={onToggleReadHandler}
                hide={showDetail}
                userProfile={userProfile}
                tabIndex={0}
              />
            ) : null}
        </StyledCustomScrollbarWrapper>

      {showDetail && (
        <NotificationDetail
          onClose={onDetailClose}
          notification={selectedNotification!}
          backHandler={detailBackHandler}
        />
      )}
      {showSummary &&
        createPortal(
          <NotificationSummaryPopup
            title={t('notification.sheet.summaryHeader') || 'Notification Summary'}
            text={linkifySummary(
              (summaryText || '').replace(/\\n/g, '\n'),
              getInsightsNotifications(),
              (n) => getLocalizationValues(userProfile, n).title || n.title || ''
            )}
            disclaimer={
              t('notification.sheet.summaryDisclaimer') ||
              '*AI-generated summary based on your notifications. AI can make mistakes.'
            }
            loadingPrefix={t('notification.sheet.summaryThinking') || 'Thinking'}
            onNotificationClick={handleSummaryNotificationClick}
            onClose={() => setShowSummary(false)}
          />,
          document.body
        )}
    </StyledNotificationSheet>
    </FocusTrap>
  );
}
